import React, { type RefObject } from 'react';

import type { Row, Table as TableProps } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { twMerge } from 'tailwind-merge';

import { Table, TableBody, TableCell, TableRow } from '@/_components';
import { cs } from '@/_shared';

import { LoaderRows } from './loader-rows';
import { isRowInteractiveTarget } from './row-interactive';
import { scrollTableRowIntoView } from './scroll-table-row-into-view';
import { TablesHeader } from './table-header';

export type DataTableInfiniteScrollProps = {
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Number of skeleton rows shown while loading the next page. @default 3 */
  loaderRowCount?: number;
};

export type DataTableProps<TData> = {
  table: TableProps<TData>;
  children?: React.ReactNode;
  containerClassName?: string;
  tabIndex?: number;
  /** When set, every row uses this exact tabindex (e.g. -1 to remove rows from the tab sequence while keeping them focusable for arrow navigation). Overrides `tabIndex`. */
  uniformRowTabIndex?: number;
  /** Callback fired when Enter is pressed on a focused data row */
  onFocusEnter?: (rowData: TData) => void;
  /** Opt-in: Arrow Up on first row returns focus here (use `useDataTableToolbarKeyboardNav`). */
  previousFocusRef?: RefObject<HTMLElement | null>;
  onRowClick?: (rowData: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Callback fired when a row receives focus (keyboard navigation). */
  onRowFocus?: (rowData: TData) => void;
  isRowActive?: (rowData: TData) => boolean;
  disableRowSelection?: boolean;
  tableHeaderClassName?: string;
  allowScroll?: boolean;
  /** Enable row virtualization — only visible rows are rendered in the DOM. */
  virtualized?: boolean;
  /** Estimated row height in px for the virtualizer (default 40). */
  estimatedRowHeight?: number;
  /** Extra `<tr>` classes from row data (e.g. muted void rows). */
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /** Opt-in infinite scroll: auto-fetches next page when the virtualizer nears the end of the list. Requires `virtualized`. */
  infiniteScroll?: DataTableInfiniteScrollProps;
  /** Square thead corners (no default rounded header cells). Useful for nested tables. */
  tableHeaderFlatCorners?: boolean;
  containOverscroll?: boolean;
  tableBodyClassName?: string;
};

export function DataTable<TData>({
  tabIndex,
  uniformRowTabIndex,
  table,
  children,
  onFocusEnter,
  previousFocusRef,
  onRowClick,
  onRowFocus,
  isRowActive,
  containerClassName,
  disableRowSelection = false,
  tableHeaderClassName = '',
  tableBodyClassName = '',
  allowScroll = true,
  virtualized = false,
  estimatedRowHeight = 40,
  getRowClassName,
  infiniteScroll,
  tableHeaderFlatCorners,
  containOverscroll,
}: DataTableProps<TData>) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const pendingFocusRef = React.useRef<number | null>(null);
  /** Blocks duplicate fetchNextPage calls before React Query sets isFetchingNextPage (virtualizer effect can run many times per scroll). */
  const fetchNextInFlightRef = React.useRef(false);
  const suppressRowFocusFromPointerRef = React.useRef(false);
  const rows = table.getRowModel().rows;

  React.useEffect(() => {
    const clear = () => {
      suppressRowFocusFromPointerRef.current = false;
    };
    document.addEventListener('pointerup', clear);
    document.addEventListener('pointercancel', clear);
    return () => {
      document.removeEventListener('pointerup', clear);
      document.removeEventListener('pointercancel', clear);
    };
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 10,
    enabled: virtualized,
  });

  const fetchNextPage = infiniteScroll?.fetchNextPage;
  const hasNextPage = infiniteScroll?.hasNextPage;
  const isFetchingNextPage = infiniteScroll?.isFetchingNextPage;
  const virtualItemsForInfiniteScroll = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    if (!virtualized || !fetchNextPage) return;

    if (!hasNextPage) {
      fetchNextInFlightRef.current = false;
      return;
    }
    if (isFetchingNextPage || fetchNextInFlightRef.current) return;

    const lastItem = virtualItemsForInfiniteScroll.at(-1);
    if (!lastItem) return;

    if (lastItem.index >= rows.length - 1) {
      fetchNextInFlightRef.current = true;
      void Promise.resolve(fetchNextPage()).finally(() => {
        fetchNextInFlightRef.current = false;
      });
    }
  }, [virtualized, fetchNextPage, hasNextPage, isFetchingNextPage, rows.length, virtualItemsForInfiniteScroll]);

  const notifyFocus = React.useCallback(
    (element: HTMLTableRowElement) => {
      if (!onRowFocus || disableRowSelection) {
        return;
      }
      const indexData = element.dataset.rowIndex;
      if (indexData == null) {
        return;
      }
      const row = rows[Number(indexData)];
      if (row) {
        onRowFocus(row.original as TData);
      }
    },
    [disableRowSelection, onRowFocus, rows]
  );

  const scrollRowIntoView = React.useCallback((row: HTMLTableRowElement) => {
    scrollTableRowIntoView(row, scrollContainerRef.current);
  }, []);

  // After a virtualizer-triggered re-render, focus the row that was out of the rendered window.
  // Scrolling and notifyFocus are handled by the row's onFocus handler.
  React.useLayoutEffect(() => {
    if (pendingFocusRef.current == null) return;
    const targetIndex = pendingFocusRef.current;
    const container = scrollContainerRef.current;
    if (!container) return;
    const targetRow = container.querySelector<HTMLTableRowElement>(`[data-row-index="${targetIndex}"]`);
    if (targetRow) {
      pendingFocusRef.current = null;
      targetRow.focus({ preventScroll: true });
    }
  });

  const handleDirectionalNav = React.useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (disableRowSelection) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const current = e.currentTarget as HTMLTableRowElement;
      const currentIndex = Number(current.dataset.rowIndex);
      if (e.key === 'ArrowUp' && currentIndex === 0 && previousFocusRef?.current) {
        previousFocusRef.current.focus();
        return;
      }
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const targetIndex = currentIndex + direction;
      if (targetIndex < 0 || targetIndex >= rows.length) return;

      const tbody = current.closest('tbody');
      const targetRow = tbody?.querySelector<HTMLTableRowElement>(`[data-row-index="${targetIndex}"]`);

      if (targetRow) {
        // Scrolling and notifyFocus handled by the row's onFocus handler.
        targetRow.focus({ preventScroll: true });
      } else if (virtualized) {
        // Target row is beyond the rendered window — scroll the virtualizer and focus after re-render.
        pendingFocusRef.current = targetIndex;
        rowVirtualizer.scrollToIndex(targetIndex, { align: 'auto' });
      }
    },
    [disableRowSelection, previousFocusRef, rows.length, rowVirtualizer, virtualized]
  );

  const renderRow = React.useCallback(
    (row: Row<TData>, index: number) => {
      const rowTabIndex =
        uniformRowTabIndex !== undefined ? uniformRowTabIndex : tabIndex !== undefined ? tabIndex + index : undefined;

      return (
        <TableRow
          key={row.id}
          tabIndex={rowTabIndex}
          data-row-index={index}
          data-expand={row.getIsExpanded() ? 'expanded' : undefined}
          data-state={row.getIsSelected() ? 'selected' : undefined}
          data-active={isRowActive?.(row.original as TData) ? 'true' : undefined}
          onPointerDownCapture={(event) => {
            suppressRowFocusFromPointerRef.current = isRowInteractiveTarget(event.target);
          }}
          onMouseDownCapture={(event) => {
            suppressRowFocusFromPointerRef.current = isRowInteractiveTarget(event.target);
          }}
          onClick={(event) => {
            if (isRowInteractiveTarget(event.target)) {
              return;
            }
            onRowClick?.(row.original as TData, event);
          }}
          className={twMerge(
            row.getParentRow()?.getIsExpanded() ? 'bg-gray-50 dark:bg-gray-900' : '',
            'data-[active=true]:bg-c-background!',
            (onRowClick || onRowFocus) && 'cursor-pointer',
            getRowClassName?.(row)
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onFocusEnter) {
              e.preventDefault();
              onFocusEnter(row.original as TData);
              return;
            }
            if (disableRowSelection) return;
            if (e.key === ' ' || e.code === 'Space' || e.key === 'Spacebar') {
              if (typeof row.getCanExpand === 'function' && row.getCanExpand()) {
                e.preventDefault();
                row.toggleExpanded();
                return;
              }
            }
            handleDirectionalNav(e);
          }}
          onFocus={(event) => {
            if (disableRowSelection) return;

            if (suppressRowFocusFromPointerRef.current) {
              return;
            }

            const rowElement = event.currentTarget as HTMLTableRowElement;
            scrollRowIntoView(rowElement);
            notifyFocus(rowElement);
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell
              key={cell.id}
              className={twMerge((cell.column.columnDef.meta as { className?: string } | undefined)?.className)}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      );
    },
    [
      tabIndex,
      uniformRowTabIndex,
      isRowActive,
      onRowClick,
      onRowFocus,
      disableRowSelection,
      onFocusEnter,
      handleDirectionalNav,
      notifyFocus,
      getRowClassName,
      scrollRowIntoView,
    ]
  );

  const renderBody = () => {
    if (!rows.length) {
      return (
        <TableRow>
          <TableCell colSpan={table.getAllColumns().length} className="text-c-card-muted h-24 text-center">
            No results.
          </TableCell>
        </TableRow>
      );
    }

    if (!virtualized) {
      return rows.map((row, index) => renderRow(row, index));
    }

    const virtualItems = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualItems[0]?.start ?? 0;
    const paddingBottom = totalSize - (virtualItems.at(-1)?.end ?? 0);

    return (
      <>
        {paddingTop > 0 && (
          <tr>
            <td style={{ height: paddingTop }} />
          </tr>
        )}
        {virtualItems.map((virtualItem) => renderRow(rows[virtualItem.index], virtualItem.index))}
        {paddingBottom > 0 && (
          <tr>
            <td style={{ height: paddingBottom }} />
          </tr>
        )}
      </>
    );
  };

  return (
    <Table
      allowScroll={allowScroll}
      containOverscroll={Boolean(containOverscroll) || Boolean(infiniteScroll)}
      containerRef={scrollContainerRef}
      containerClassName={twMerge('border-c-border border', 'rounded-xl', cs('rounded-4xl'), containerClassName)}
    >
      <TablesHeader table={table} className={tableHeaderClassName} flatRoundedCorners={tableHeaderFlatCorners} />
      <TableBody className={tableBodyClassName}>{renderBody()}</TableBody>
      {infiniteScroll?.isFetchingNextPage && (
        <LoaderRows columnCount={table.getAllColumns().length} rowCount={infiniteScroll.loaderRowCount} />
      )}
      {children}
    </Table>
  );
}
